import { QuestionStatus } from '@exam-management/graphql';
import { Button, Divider, Flex, Paper, Text, VisibilityOutlinedIcon } from '@shared/components';

import { PREVIEWABLE_QUESTION_STATUSES } from '../../constants';
import { type QContentDetailFormContextData } from '../../types';
import { DetailSection } from '../detail-section';
import { InformationSection } from '../information-section';
import { OwnerSection } from '../owner-section';
import { PropertiesSection } from '../properties-section';
import { QContentDetailFormActions } from '../qcontent-details-form-actions';

interface QContentDetailsSectionProps {
  onSubmit: (data: QContentDetailFormContextData) => Promise<void>;
  onSubmitAndCreateNew: (data: QContentDetailFormContextData) => Promise<void>;
  onPreviewQuestion?: () => void;
  onClickUpdatePUseModal?: () => void;
  questionId?: string;
  readonly?: boolean;
  questionStatus?: QuestionStatus;
}

export default function QContentDetailsSection(props: Readonly<QContentDetailsSectionProps>) {
  const {
    onSubmit,
    onSubmitAndCreateNew,
    onPreviewQuestion,
    onClickUpdatePUseModal,
    questionId,
    readonly: isReadonly = false,
    questionStatus = QuestionStatus.Draft,
  } = props;

  const shouldDisableQuestionPreview =
    !questionId || !PREVIEWABLE_QUESTION_STATUSES.includes(questionStatus);

  return (
    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(6), width: '100%' }}>
      <Paper sx={{ flex: 1 }}>
        <Flex
          sx={(theme) => ({
            justifyContent: 'space-between',
            p: theme.spacing(4, 6),
          })}
        >
          <Text variant='h2'>Question Details</Text>
          <Button
            color='secondary'
            variant='outlined'
            startIcon={<VisibilityOutlinedIcon />}
            onClick={onPreviewQuestion}
            disabled={shouldDisableQuestionPreview}
          >
            Preview Question
          </Button>
        </Flex>
        <Divider />
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(10),
            p: theme.spacing(6),
          })}
        >
          <InformationSection
            readonly={isReadonly}
            questionStatus={questionStatus}
            onClick={onClickUpdatePUseModal}
          />
          <DetailSection
            questionId={questionId}
            readonly={isReadonly}
            questionStatus={questionStatus}
          />
          <PropertiesSection
            questionId={questionId}
            readonly={isReadonly}
            questionStatus={questionStatus}
          />
          <OwnerSection readonly={isReadonly} questionStatus={questionStatus} />
        </Flex>
      </Paper>
      <QContentDetailFormActions
        onSubmit={onSubmit}
        onSubmitAndCreateNew={onSubmitAndCreateNew}
        questionStatus={questionStatus}
      />
    </Flex>
  );
}
